import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiKeyResponse, CreateExchangeRequest, CreateChainRequest, CreateConnectionRequest, UserConnectionsResponse, CreateConnectionResponse, SyncTransactionResponse, SyncJobStatusResponse, OAuthAuthorizeResponse } from '../models/integrations-management.models';

@Injectable({
  providedIn: 'root'
})
export class IntegrationsManagementApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  getChainsAndExchanges(searchQuery?: string): Observable<ApiKeyResponse> {
    let params = new HttpParams();
    if (searchQuery && searchQuery.trim()) {
      params = params.set('search', searchQuery.trim());
    }
    return this.http.get<ApiKeyResponse>(`${this.baseUrl}/user/exchanges-chains`, { params });
  }

  /**
   * Get all user connections (chains and exchanges)
   * GET /user/connections
   */
  getConnections(): Observable<UserConnectionsResponse> {
    return this.http.get<UserConnectionsResponse>(`${this.baseUrl}/user/connections`);
  }

  /**
   * Create a new connection (chain or exchange)
   * POST /user/connections
   */
  createConnection(payload: CreateConnectionRequest): Observable<CreateConnectionResponse> {
    return this.http.post<CreateConnectionResponse>(`${this.baseUrl}/user/connections`, payload);
  }

  /**
   * Delete a connection by ID
   * DELETE /user/connections/:connectionId
   */
  /** Rename a connection — the only field the Integrations page edits. */
  updateConnectionLabel(connectionId: string, label: string): Observable<CreateConnectionResponse> {
    return this.http.put<CreateConnectionResponse>(`${this.baseUrl}/user/connections/${connectionId}`, { label });
  }

  deleteConnection(connectionId: string): Observable<ApiKeyResponse> {
    return this.http.delete<ApiKeyResponse>(`${this.baseUrl}/user/connections/${connectionId}`);
  }

  getOAuthAuthorizeUrl(provider: string, exchangeId: string): Observable<OAuthAuthorizeResponse> {
    const params = new HttpParams().set('exchange_id', exchangeId);
    return this.http.get<OAuthAuthorizeResponse>(
      `${this.baseUrl}/user/connections/oauth/${encodeURIComponent(provider)}/authorize`,
      { params },
    );
  }

  getChains(): Observable<ApiKeyResponse> {
    return this.http.get<ApiKeyResponse>(`${this.baseUrl}/user/connections/chains`);
  }

  createChain(payload: CreateChainRequest): Observable<ApiKeyResponse> {
    return this.http.post<ApiKeyResponse>(`${this.baseUrl}/user/connections/chains`, payload);
  }

  getChainByConnectionId(connectionId: string): Observable<ApiKeyResponse> {
    return this.http.get<ApiKeyResponse>(`${this.baseUrl}/user/connections/chains/${connectionId}`);
  }

  deleteChainByConnectionId(connectionId: string): Observable<ApiKeyResponse> {
    return this.http.delete<ApiKeyResponse>(`${this.baseUrl}/user/connections/chains/${connectionId}`);
  }

  createExchange(payload: CreateExchangeRequest): Observable<ApiKeyResponse> {
    return this.http.post<ApiKeyResponse>(`${this.baseUrl}/user/connections/exchanges`, payload);
  }

  getExchanges(): Observable<ApiKeyResponse> {
    return this.http.get<ApiKeyResponse>(`${this.baseUrl}/user/connections/exchanges`);
  }

  getExchangeByConnectionId(connectionId: string): Observable<ApiKeyResponse> {
    return this.http.get<ApiKeyResponse>(`${this.baseUrl}/user/connections/exchanges/${connectionId}`);
  }

  deleteExchangeByConnectionId(connectionId: string): Observable<ApiKeyResponse> {
    return this.http.delete<ApiKeyResponse>(`${this.baseUrl}/user/connections/exchanges/${connectionId}`);
  }

  /**
   * Start async sync for a connection
   * POST /transactions/sync
   */
  syncTransactions(connectionId: string): Observable<SyncTransactionResponse> {
    return this.http.post<SyncTransactionResponse>(`${this.baseUrl}/transactions/sync`, {
      connection_id: connectionId
    });
  }

  /**
   * Poll sync job status
   * GET /transactions/sync/status/:jobId
   */
  getSyncJobStatus(jobId: string): Observable<SyncJobStatusResponse> {
    return this.http.get<SyncJobStatusResponse>(`${this.baseUrl}/transactions/sync/status/${jobId}`);
  }
}
